/**
 * Onboarding step 2 of 7 — Pick a genie.
 *
 * The "what can a genie do" resell + the starting-point picker. Renders 7
 * featured genie types from the REAL catalogue (`playbook-templates.ts`) plus a
 * "Something else" custom tile, Reception pre-selected. Selection writes the
 * chosen GenieTypeChoice into OnboardingContext so later steps see it. A "Browse
 * all" link opens a sheet over the full 37-genie catalogue.
 *
 * Brand law: WelcomeFrame chrome + signature gradient; genie-* tokens only;
 * "Help Genie" two words; *Voice* italic (in frame). No "AI" language; no em dashes.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { WelcomeFrame } from "@/components/voice-onboarding/WelcomeFrame";
import { ONBOARD_ROUTES, VOICE_ONBOARD_STEPS } from "./steps";
import { useOnboarding } from "@/contexts/voice-onboarding/OnboardingContext";
import { FEATURED_TILES, GenieTypeTile, BrowseAllSheet } from "./PickTiles";
import { playbookGoals } from "@/pages/UserHub/Playbooks/data/playbook-templates";
import type { GenieTypeChoice } from "@/types/voice-vision";

const TOTAL_GENIE_COUNT = playbookGoals.reduce((n, g) => n + g.plays.length, 0);

/** Strip the rich tile down to the context-stored GenieTypeChoice shape. */
const toChoice = ({ id, title, blurb, mascot, category }: GenieTypeChoice): GenieTypeChoice => ({
  id,
  title,
  blurb,
  mascot,
  category,
});

const PickPage = () => {
  const navigate = useNavigate();
  const { genieType, setGenieType } = useOnboarding();
  const [browseOpen, setBrowseOpen] = useState(false);

  // Reception pre-selected on first arrival (only if nothing chosen yet).
  useEffect(() => {
    if (!genieType) {
      const reception = FEATURED_TILES.find((t) => t.id === "reception");
      if (reception) setGenieType(toChoice(reception));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedId = genieType?.id ?? null;

  // A choice picked from the Browse sheet that isn't one of the 7 featured tiles.
  const offGridChoice = useMemo(() => {
    if (!genieType) return null;
    return FEATURED_TILES.some((t) => t.id === genieType.id) ? null : genieType;
  }, [genieType]);

  return (
    <WelcomeFrame
      step={2}
      steps={VOICE_ONBOARD_STEPS}
      eyebrow="WHAT SHOULD YOURS DO?"
      title="Pick a starting point"
      subtitle="Every genie is a job. Start from one of these, then change anything later. This is just what we set up first."
      showMascot={false}
      maxWidth={720}
      continueLabel="Continue"
      continueDisabled={!genieType}
      onBack={() => navigate(ONBOARD_ROUTES[0])}
      onContinue={() => navigate(ONBOARD_ROUTES[2])}
      onSkip={() => navigate("/genies")}
    >
      <div className="w-full flex flex-col">
        {/* genie-types grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FEATURED_TILES.map((tile) => (
            <GenieTypeTile
              key={tile.id}
              tile={tile}
              selected={selectedId === tile.id}
              onSelect={() => setGenieType(toChoice(tile))}
            />
          ))}
        </div>

        {/* off-grid selection made from Browse all */}
        {offGridChoice && (
          <p className="text-center text-[13px] text-genie-text-sub mt-4">
            Selected{" "}
            <span className="font-bold text-genie-text-primary">{offGridChoice.title}</span> from the
            full catalogue.
          </p>
        )}

        {/* Browse all link */}
        <button
          type="button"
          onClick={() => setBrowseOpen(true)}
          className="self-center inline-flex items-center gap-1.5 mt-6 text-[13px] font-semibold text-genie-primary hover:opacity-80 transition-opacity"
        >
          Browse all {TOTAL_GENIE_COUNT} genie types
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      <BrowseAllSheet
        open={browseOpen}
        onOpenChange={setBrowseOpen}
        selectedId={selectedId}
        onPick={(choice) => {
          setGenieType(choice);
          setBrowseOpen(false);
        }}
      />
    </WelcomeFrame>
  );
};

export default PickPage;
